import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { admin, typography } from '../../../../styles/tokens';
import SpreadsheetPath from '../spreadsheet/SpreadsheetPath';
import CompetitorGrid from './CompetitorGrid';
import ExportInstructionsOverlay from './ExportInstructionsOverlay';
import type { CompetitorId, CompetitorImportProfile } from './competitorImportProfiles';

interface CompetitorPathProps {
  onBackToGrid: () => void;
  onSaved: () => void;
}

type Mode =
  | { kind: 'grid' }
  | { kind: 'instructions'; profile: CompetitorImportProfile }
  | { kind: 'flow'; profile: CompetitorImportProfile | null };

export default function CompetitorPath({
  onBackToGrid,
  onSaved,
}: CompetitorPathProps) {
  const [mode, setMode] = useState<Mode>({ kind: 'grid' });
  // Per-session dismissed-instructions flags so re-entering a card after
  // "I already have the file" skips the overlay for that competitor.
  const [skippedOverlays, setSkippedOverlays] = useState<Set<CompetitorId>>(
    () => new Set(),
  );

  const handlePickCompetitor = (profile: CompetitorImportProfile) => {
    if (skippedOverlays.has(profile.id)) {
      setMode({ kind: 'flow', profile });
    } else {
      setMode({ kind: 'instructions', profile });
    }
  };

  const handlePickCatchAll = () => {
    setMode({ kind: 'flow', profile: null });
  };

  const handleInstructionsContinue = () => {
    if (mode.kind !== 'instructions') return;
    setMode({ kind: 'flow', profile: mode.profile });
  };

  const handleInstructionsSkip = () => {
    if (mode.kind !== 'instructions') return;
    setSkippedOverlays(prev => {
      const next = new Set(prev);
      next.add(mode.profile.id);
      return next;
    });
    setMode({ kind: 'flow', profile: mode.profile });
  };

  // Delegates to the shared shell — once we're in a flow, SpreadsheetPath
  // owns the chrome. `onBackToGrid` on the flow returns to our own grid
  // (not the entry grid) so the user can pick a different competitor.
  if (mode.kind === 'flow') {
    return (
      <SpreadsheetPath
        onBackToGrid={() => setMode({ kind: 'grid' })}
        onSaved={onSaved}
        competitorProfile={mode.profile}
        defaultSourceOverride={mode.profile ? undefined : 'competitor'}
      />
    );
  }

  if (mode.kind === 'instructions') {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '8px 4px 0' }}>
        <button
          type="button"
          onClick={() => setMode({ kind: 'grid' })}
          style={backButtonStyle}
        >
          <ChevronLeft size={16} />
          <span>Pick a different tool</span>
        </button>
        <h1 style={headerStyle}>Before you upload</h1>
        <p style={{ ...leadStyle, marginBottom: 20 }}>
          Here&rsquo;s how to get the file out of {mode.profile.displayName}.
          It usually takes about a minute.
        </p>
        <ExportInstructionsOverlay
          profile={mode.profile}
          onContinue={handleInstructionsContinue}
          onSkip={handleInstructionsSkip}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '8px 4px 0' }}>
      <button type="button" onClick={onBackToGrid} style={backButtonStyle}>
        <ChevronLeft size={16} />
        <span>Import your clients</span>
      </button>
      <h1 style={headerStyle}>Import from another tool</h1>
      <p style={{ ...leadStyle, marginBottom: 24 }}>
        Pick the tool you&rsquo;re using today. We&rsquo;ll walk you through
        getting your client list out and back in here.
      </p>
      <CompetitorGrid
        onPickCompetitor={handlePickCompetitor}
        onPickCatchAll={handlePickCatchAll}
      />
    </div>
  );
}

const backButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '6px 8px 6px 4px',
  marginBottom: 16,
  background: 'transparent',
  border: 'none',
  color: admin.caption,
  fontSize: typography.size.caption,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const headerStyle: React.CSSProperties = {
  margin: 0,
  fontSize: typography.size.title,
  fontWeight: typography.weight.bold,
  color: admin.header,
  letterSpacing: '-0.01em',
  marginBottom: 8,
};

const leadStyle: React.CSSProperties = {
  margin: 0,
  fontSize: typography.size.body,
  color: admin.body,
  lineHeight: typography.lineHeight.relaxed,
};

